import { Close as CloseIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { circularProgressSizeProps } from '@opengov/capital-mui-theme';
import { IStory, IStoryContent } from '@opengov/stories-shared/types';
import { useQueryClient } from '@tanstack/react-query';
import noop from 'lodash/noop';
import React, { useEffect, useRef, useState } from 'react';
import { IUpdateStoryContentProps } from '../../../../../types/storyViewTypes';
import { assertNotNil } from '../../../../../types/typeUtils';
import { CUSTOM_SPREADSHEET_REPORT_MEDIA_TYPE } from '../../../../constants/modals';
import {
  addMedia,
  replaceTitleInSpreadsheetTableMarkup,
  wrapHtmlWithTitleAndSpreadsheetCommentMarkers
} from '../../../../utils/dom';
import { useToast } from '../../../Toast/context/useToast.context';
import { LoadingSkeleton } from '../AddReportTableModal/LoadingSkeleton';
import locale from '../customReports-locale.json';
import { useDeleteSpreadsheetTable } from '../hooks/useDeleteSpreadsheetTable';
import { useFetchNamedRangeMarkup } from '../hooks/useFetchNamedRangeMarkup';
import { usePostSpreadsheetTable } from '../hooks/usePostSpreadsheetTable';
import { usePutSpreadsheetTable } from '../hooks/usePutSpreadsheetTable';
import { styles } from './EditCustomSpreadsheetMediaModal.styles';
import { clearTableMarkupFromStoryContent } from './EditCustomSpreadsheetMediaModal.utils';
import { editCustomSpreadsheetModalQueryKeys } from './hooks/editCustomSpreadsheetModalQueryKeys';
import { useFetchNamedRangesForEditMediaModal } from './hooks/useFetchNamedRangesForEditMediaModal';
import { useFetchSpreadsheetsForEditMediaModal } from './hooks/useFetchSpreadsheetsForEditMediaModal';
import { useFetchStoryContentSpreadsheetReferences } from './hooks/useFetchStoryContentSpreadsheetReferences';
import { publicationCustomReportPath } from '../../../../../routes/paths';

type Props = {
  onClose: () => void;
  entityId: number;
  entitySubdomain: string;
  story: IStory;
  storyContent: IStoryContent;
  publicationId: string;
  mediaIndex: number;
  updateStoryContent: (storyContent: IStoryContent) => void;
  updateStoryContentDB: (props: IUpdateStoryContentProps) => void;
};

type InitialValues = {
  spreadsheetId: string;
  namedRangeId: string;
  title: string;
};

export function EditCustomSpreadsheetMediaModal({
  onClose,
  entityId,
  entitySubdomain,
  story,
  storyContent,
  publicationId,
  mediaIndex,
  updateStoryContent,
  updateStoryContentDB
}: Props) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();
  const [selectedSpreadsheetId, setSelectedSpreadsheetId] = useState('');
  const [selectedNamedRangeId, setSelectedNamedRangeId] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const initialValues = useRef<InitialValues | null>(null);

  const { data: spreadsheets, isLoading: isLoadingSpreadsheets } = useFetchSpreadsheetsForEditMediaModal({
    publicationId
  });

  const {
    data: namedRanges,
    isLoading: rangesQueryLoading,
    isFetching: rangesFetching
  } = useFetchNamedRangesForEditMediaModal({ spreadsheetId: selectedSpreadsheetId });
  const isLoadingNamedRanges = rangesQueryLoading && rangesFetching;

  const { data: referencesData, isLoading: isLoadingReferences } = useFetchStoryContentSpreadsheetReferences({
    storyContentId: storyContent._id
  });

  const { mutateAsync: postSpreadsheetTable } = usePostSpreadsheetTable({
    entityId,
    publicationId,
    storyId: story._id,
    storyContentId: storyContent._id
  });

  const { mutateAsync: putSpreadsheetTable } = usePutSpreadsheetTable({
    publicationId,
    storyId: story._id,
    storyContentId: storyContent._id
  });

  const { mutateAsync: deleteSpreadsheetTable } = useDeleteSpreadsheetTable({
    storyId: story._id,
    storyContentId: storyContent._id
  });

  const { refetch: refetchMarkup } = useFetchNamedRangeMarkup({
    publicationId,
    spreadsheetId: selectedSpreadsheetId,
    namedRangeId: selectedNamedRangeId,
    enabled: false
  });

  // Check if form has been modified
  const hasChanges =
    initialValues.current !== null &&
    (selectedSpreadsheetId !== initialValues.current.spreadsheetId ||
      selectedNamedRangeId !== initialValues.current.namedRangeId ||
      titleValue !== initialValues.current.title);

  // Synching initial values from storyContentSpreadsheetReferences
  useEffect(() => {
    if (referencesData && referencesData.length > 0) {
      const preselectedReference = referencesData[0];
      const spreadsheetId = preselectedReference?.spreadsheetId ?? '';
      const namedRangeId = preselectedReference?.namedRangeId ?? '';
      const title = preselectedReference?.title ?? '';

      setSelectedSpreadsheetId(spreadsheetId);
      setSelectedNamedRangeId(namedRangeId);
      setTitleValue(title);

      // Store initial values for comparison later (only set once)
      if (initialValues.current === null) {
        initialValues.current = { spreadsheetId, namedRangeId, title };
      }
    }
  }, [referencesData]);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      // Remove table markup from storyContent
      const newStoryContent = clearTableMarkupFromStoryContent({ storyContent, mediaIndex });
      updateStoryContent(newStoryContent);
      updateStoryContentDB({ story: story, isTemplate: false, storyContent: newStoryContent });

      // Delete spreadsheet table
      const initial = assertNotNil(initialValues?.current);
      await deleteSpreadsheetTable({ spreadsheetId: initial.spreadsheetId, namedRangeId: initial.namedRangeId });
      void queryClient.invalidateQueries({
        queryKey: editCustomSpreadsheetModalQueryKeys.storyContentSpreadsheetReferences(storyContent._id)
      });
    } catch (error) {
      console.error(error);
      toggleToast({
        title: locale.errors.somethingWentWrong,
        body: locale.errors.unableToDeleteSpreadsheetTable,
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
      onClose();
    }
  };

  const handleTitleChange = async () => {
    const newStoryContent = replaceTitleInStoryContent({ storyContent, mediaIndex, newTitle: titleValue });
    updateStoryContent(newStoryContent);
    updateStoryContentDB({ story: story, isTemplate: false, storyContent: newStoryContent });

    // Update title in reference as well
    await putSpreadsheetTable({
      spreadsheetId: selectedSpreadsheetId,
      namedRangeId: selectedNamedRangeId,
      title: titleValue
    });

    void queryClient.invalidateQueries({
      queryKey: editCustomSpreadsheetModalQueryKeys.storyContentSpreadsheetReferences(storyContent._id)
    });
  };

  const handleNameRangeChange = async () => {
    /**
     * Step 1: Remove markup from storyContent and delete storyContent spreadsheet reference
     */

    const newStoryContent = clearTableMarkupFromStoryContent({ storyContent, mediaIndex });
    updateStoryContent(newStoryContent);
    updateStoryContentDB({ story: story, isTemplate: false, storyContent: newStoryContent });

    if (initialValues.current) {
      await deleteSpreadsheetTable({
        spreadsheetId: initialValues.current.spreadsheetId,
        namedRangeId: initialValues.current.namedRangeId
      });
    }

    /**
     * Step 2: Call /markup for newly selected name range, insert title via wrapHtmlWithTitleAndSpreadsheetCommentMarkers, and save to DB (same as the flow for adding a new table)
     */

    const { data: namedRangeMarkup } = await refetchMarkup();
    const htmlContent = assertNotNil(namedRangeMarkup?.html);

    const demarcatedHtmlContent = wrapHtmlWithTitleAndSpreadsheetCommentMarkers({
      content: htmlContent,
      title: titleValue
    });

    const storyContentWithUpdatedMarkup = addMedia(
      newStoryContent,
      demarcatedHtmlContent,
      mediaIndex,
      CUSTOM_SPREADSHEET_REPORT_MEDIA_TYPE,
      true
    );

    updateStoryContent(storyContentWithUpdatedMarkup);
    updateStoryContentDB({
      story: story,
      isTemplate: false,
      storyContent: storyContentWithUpdatedMarkup
    });

    /**
     * Step 3:  POST /spreadsheet-table to update the reference.
     */
    await postSpreadsheetTable({
      entityId,
      publicationId,
      storyId: story._id,
      storyContentId: storyContent._id,
      spreadsheetId: selectedSpreadsheetId,
      namedRangeId: selectedNamedRangeId,
      title: titleValue
    });

    void queryClient.invalidateQueries({
      queryKey: editCustomSpreadsheetModalQueryKeys.storyContentSpreadsheetReferences(storyContent._id)
    });
  };

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      const didSelectedNameRangeChange = selectedNamedRangeId !== initialValues.current?.namedRangeId;
      const didTitleChange = titleValue !== initialValues.current?.title;
      if (didSelectedNameRangeChange) {
        // This also handles title change if it was changed at the same time
        await handleNameRangeChange();
      } else if (didTitleChange) {
        // This handles title change if it was changed at the same time
        await handleTitleChange();
      }
    } catch (error) {
      console.error(error);
      toggleToast({
        title: locale.errors.somethingWentWrong,
        body: locale.errors.unableToUpdateCustomReport,
        severity: 'error'
      });
    } finally {
      setIsUpdating(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth={true}
      data-test="add-custom-spreadsheet-modal"
      disableEscapeKeyDown
    >
      <DialogTitle sx={styles.dialogTitle}>
        {locale.editCustomSpreadsheetMediaModal.title}
        <Button variant="outlined" onClick={onClose} startIcon={<CloseIcon />} sx={styles.actionButton}>
          {locale.editCustomSpreadsheetMediaModal.close}
        </Button>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent} dividers={true}>
        <Box sx={styles.dialogContentBox}>
          {isLoadingSpreadsheets || isLoadingReferences ? (
            <LoadingSkeleton />
          ) : (
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  value={selectedSpreadsheetId}
                  onChange={(e) => {
                    setSelectedSpreadsheetId(e.target.value);
                    setSelectedNamedRangeId('');
                  }}
                  select
                  label={locale.editCustomSpreadsheetMediaModal.customReport}
                  sx={styles.textField}
                >
                  {spreadsheets?.spreadsheets?.length ? (
                    spreadsheets.spreadsheets.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      {locale.editCustomSpreadsheetMediaModal.noCustomReportsAvailable}
                    </MenuItem>
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  value={selectedNamedRangeId}
                  onChange={(e) => setSelectedNamedRangeId(e.target.value)}
                  select
                  label={locale.editCustomSpreadsheetMediaModal.namedRange}
                  sx={styles.textField}
                  disabled={!selectedSpreadsheetId || isLoadingNamedRanges}
                  InputProps={{
                    startAdornment: isLoadingNamedRanges ? (
                      <InputAdornment position="start">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : undefined
                  }}
                >
                  {namedRanges?.spreadsheetNamedRanges?.length ? (
                    namedRanges.spreadsheetNamedRanges.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      {!selectedSpreadsheetId
                        ? locale.editCustomSpreadsheetMediaModal.selectCustomReportFirst
                        : locale.editCustomSpreadsheetMediaModal.noNamedRangesAvailable}
                    </MenuItem>
                  )}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body1" sx={styles.infoText}>
                  These can be created and managed in the{' '}
                  <Link
                    href={publicationCustomReportPath(entitySubdomain, publicationId)}
                    sx={styles.customReportsLink}
                  >
                    {locale.editCustomSpreadsheetMediaModal.customReports}
                  </Link>{' '}
                  section of the Publication. {locale.editCustomSpreadsheetMediaModal.customReportsFullWidthLayout}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  label={locale.editCustomSpreadsheetMediaModal.titlePlaceholder}
                  sx={styles.titleTextField}
                  inputProps={{ className: 'no-format' }} //Prevent ovid from overriding
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  value={'refresh-on-upload'}
                  onChange={noop}
                  label={locale.editCustomSpreadsheetMediaModal.dataUpdateFrequency}
                  disabled={true}
                  sx={styles.textField}
                  select
                >
                  <MenuItem key={1} value="refresh-on-upload">
                    {locale.editCustomSpreadsheetMediaModal.refreshOnUpload}
                  </MenuItem>
                </TextField>
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button
          onClick={onClose}
          disabled={false}
          variant="outlined"
          sx={styles.actionButton}
          color="secondary"
          data-test="edit-custom-spreadsheet-media-modal-cancel-button"
        >
          {locale.editCustomSpreadsheetMediaModal.cancel}
        </Button>
        <Button
          onClick={handleDelete}
          disabled={isDeleting || isUpdating}
          variant="contained"
          sx={styles.actionButton}
          color="error"
          endIcon={
            isDeleting ? <CircularProgress {...circularProgressSizeProps.extraXSmall} sx={styles.loadingIcon} /> : null
          }
          data-test="edit-custom-spreadsheet-media-modal-remove-button"
        >
          {locale.editCustomSpreadsheetMediaModal.remove}
        </Button>
        <Button
          onClick={handleUpdate}
          disabled={isUpdating || !hasChanges || !selectedSpreadsheetId || !selectedNamedRangeId || isDeleting}
          variant="contained"
          sx={styles.actionButton}
          endIcon={
            isUpdating ? <CircularProgress {...circularProgressSizeProps.extraXSmall} sx={styles.loadingIcon} /> : null
          }
          data-test="edit-custom-spreadsheet-media-modal-update-button"
        >
          {locale.editCustomSpreadsheetMediaModal.update}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function replaceTitleInStoryContent({
  storyContent,
  mediaIndex,
  newTitle
}: {
  storyContent: IStoryContent;
  mediaIndex: number;
  newTitle: string;
}) {
  const newStoryContent = replaceTitleInSpreadsheetTableMarkup({
    storyContentContent: storyContent.content,
    mediaIndex,
    newTitle
  });
  return { ...storyContent, shouldUpdate: true, content: newStoryContent };
}
